import { PvaData } from "@inrange/api-clients";
import React from "react";
import { Button } from "react-bootstrap";

interface PvBuildInfoProps {
  pvaData: PvaData;
  onForceRebuild: () => void;
  // REQUIRED, deliberately. A locked site is contracted: its design must not change. Every other
  // control that can alter the design sits inside a <fieldset disabled={site.locked}>. This one
  // renders between those fieldsets, so it has to carry the lock itself.
  disabled: boolean;
}

// The rebuild control is a real <Button>, never a <span onClick>: a <span> ignores the surrounding
// <fieldset disabled> (fieldset only disables form controls), so making it a button is the only way
// `disabled` — the site lock — actually holds, and it also gets keyboard focus. It is rendered as a
// SIBLING of the label, not a child; see the return blocks below for why that matters.
const rebuildButtonStyle: React.CSSProperties = {
  marginLeft: "8px",
};

const pad = (n: number): string => String(n).padStart(2, "0");

// "YYYY-MM-DD at HH:MM:SS" in the viewer's local timezone — the same zone the rest of the site page
// shows times in. `toLocaleString()` was locale-dependent (dd/mm/yyyy for some viewers); this is
// stable for everyone.
const formatBuiltAt = (isoTime?: string): string | undefined => {
  if (!isoTime) {
    return undefined;
  }
  const date = new Date(isoTime);
  if (isNaN(date.getTime())) {
    return undefined;
  }
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    ` at ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
  );
};

// A one-line summary of how the current design was built, shown just above the
// site map. Auto builds name the detection engine (V1 = Canny, V2 = SAM) and
// offer a rebuild; manual builds (user-supplied roof objects) just report
// that detection was not used.
const PvBuildInfo: React.FC<PvBuildInfoProps> = ({
  pvaData,
  onForceRebuild,
  disabled,
}) => {
  const builtAt = formatBuiltAt(pvaData.task_metrics?.start_time);
  if (!builtAt) {
    // No completed build yet - nothing to report.
    return null;
  }

  // Manual first: a manual build uses no detector at all, so nothing below may offer to choose one.
  //
  // The predicate must be the SERVER'S, not a plausible-looking approximation of it. resolve_mode
  // is unambiguous — "custom objects -> manual (detection is moot; overrides everything)" — and
  // use_custom_objects is derived server-side from the objects actually stored for the site
  // (metrics.py: `options["use_custom_objects"] = "custom_objects" in response`). It overrides even
  // an explicit engine pin. So the presence of custom objects IS the whole test.
  //
  // The engine MARKER cannot stand in for it, because the two drift. A site built auto-V2 keeps the
  // auto-V2 marker when custom objects are added later (and a failed run carries the previous marker
  // forward rather than re-deriving it). Gating on `!builtWith` would then read that site as AUTO,
  // offer the V1/V2 picker, and the server would discard the choice — a control that does nothing.
  const builtWith = pvaData.built_with_rooftop_engine_version;
  const isManual = builtWith === "manual" || !!pvaData.custom_objects;

  // A FAILED run still writes task-results, and on a first-ever build it carries no engine forward —
  // so infer_built_with falls through and reports "auto-V1". Rendered naively, a site whose only
  // build was a FAILED auto-V2 run reads "Built on <time> using automated roof object detection V1":
  // wrong in both halves, and wrong precisely during a V2 incident.
  //
  // `=== false`, not `!success`: task_run_success is required INSIDE an optional task_metrics, so on
  // a legacy response that lacks it, `undefined === false` is false and a successful old build still
  // renders normally. `!success` would mislabel every one of them as failed.
  //
  // A failed AUTO build MUST still offer the rebuild. It is the one moment someone most needs to
  // re-run the site on the other engine, and Rebuild... is the only route to the engine picker —
  // PvForceRebuildModal is the only UI that sends rooftop_engine_version, and the debug panel's
  // rebuild sends none, so the server just re-runs the engine that has already failed.
  //
  // A failed MANUAL build must NOT: the server derives `manual` from the presence of custom objects
  // and ignores any engine you pick, so the picker would be a control that does nothing. Retry a
  // manual build from the debug panel, which is what it is for.
  if (pvaData.task_metrics?.task_run_success === false) {
    const failedLabel = (
      <span className="form-label" data-testid="pv-build-info">
        Last build <strong>failed</strong>, started <strong>{builtAt}</strong>{" "}
      </span>
    );
    if (isManual) {
      // Block-level wrapper, matching the auto branch below: the label is an
      // inline <span>, and the site map's <Form.Label> renders inline too, so
      // without a block container the two collapse onto one line.
      return <div>{failedLabel}</div>;
    }
    return (
      <div>
        {failedLabel}
        <Button
          variant="secondary"
          size="sm"
          style={rebuildButtonStyle}
          onClick={onForceRebuild}
          disabled={disabled}
          data-testid="pv-force-rebuild"
        >
          Rebuild...
        </Button>
      </div>
    );
  }

  if (isManual) {
    // Block-level wrapper, matching the auto branch below. A bare inline <span>
    // collapses onto the same line as the following site map's <Form.Label>
    // (also inline); wrapping in a <div> puts "Site Map:" on its own line.
    return (
      <div>
        <span className="form-label" data-testid="pv-build-info">
          Built on <strong>{builtAt}</strong> using manually specified roof
          objects
        </span>
      </div>
    );
  }

  // ABSENT is not the same as UNRECOGNISED, and conflating them rendered an empty <strong>.
  //
  //   absent       -> "V1". The server infers exactly this when the marker is missing
  //                   (rooftop_engine.infer_built_with: manual if custom objects, else auto-V1), and
  //                   it is missing on every site built before the engine work — i.e. the default on
  //                   any environment whose pv-api predates it. Rendering nothing there is a lie by
  //                   omission on the line someone reads to decide whether to rebuild.
  //   unrecognised -> the raw value. A future auto-V3 must NOT display as "V1"; showing what the
  //                   server actually said is the honest failure mode.
  const engineLabel =
    builtWith === undefined || builtWith === null
      ? "V1"
      : builtWith === "auto-V2"
        ? "V2"
        : builtWith === "auto-V1"
          ? "V1"
          : builtWith;
  // The Button is a SIBLING of the label, never a child of it. The label is a <span>, not a
  // <label>, so it cannot adopt the button as a labeled control — that adoption is what once made
  // clicking the "Built on ... detection V2" text open the rebuild modal. Keeping them siblings (and
  // the label a non-<label> element) makes that class of bug structurally impossible.
  return (
    <div>
      <span className="form-label" data-testid="pv-build-info">
        Built on <strong>{builtAt}</strong> using automated roof object
        detection <strong>{engineLabel}</strong>{" "}
      </span>
      <Button
        variant="secondary"
        size="sm"
        style={rebuildButtonStyle}
        onClick={onForceRebuild}
        disabled={disabled}
        data-testid="pv-force-rebuild"
      >
        Rebuild...
      </Button>
    </div>
  );
};

export default PvBuildInfo;
